import { heldThreadTimelines } from "../chat/heldThreadTimeline";
import { invalidateDomainApiSession } from "../../state/domainApi";
import { purgeWorkspaceStorage } from "../../workspaceStoragePurge";
import { WORKOS_SESSION_STORAGE_KEY } from "./PairingRouteSurface";

const WORKOS_LOGOUT_URL = "https://api.workos.com/user_management/sessions/logout";

/** Set once a sign-out has reached its redirect; nothing finishes twice. */
let signOutFinished = false;

/**
 * The page navigates away after a finish, so this is only needed by tests
 * and by a page the browser restores from its back/forward cache — module
 * state comes back with it, and a fresh sign-out must be able to finish.
 */
export function resetSignOutState(): void {
  signOutFinished = false;
}

if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) resetSignOutState();
  });
}

export interface SignOutLocation {
  readonly origin: string;
  assign(url: string): void;
}

export interface SignOutOptions {
  /** Origin of the workspace server whose session cookie is being revoked. */
  readonly backendOrigin: string;
  readonly fetchImpl?: typeof fetch;
  /** How long the revoke request may take before sign-out is reported as not done. */
  readonly revokeTimeoutMs?: number;
  /**
   * Read lazily: touching `window.localStorage` can itself throw a
   * `SecurityError`, and that must only cost the WorkOS hop, not the redirect.
   */
  readonly readStorage?: () => Pick<Storage, "getItem" | "removeItem">;
  readonly location?: SignOutLocation;
  /** Clears the workspace content this browser holds; defaults to the real purge. */
  readonly purgeStorage?: () => Promise<void>;
}

/**
 * The revoke request did not answer in time. Nothing was redirected: the
 * session may still be live server-side, so the person is told and can try
 * again rather than being shown a sign-in page while still signed in.
 */
export class SignOutTimeoutError extends Error {
  constructor() {
    super("Sign-out did not complete in time. Please try again.");
    this.name = "SignOutTimeoutError";
  }
}

/**
 * Signs the browser session out: revokes the workspace session against the
 * backend (which expires the cookie), purges the workspace content this
 * browser holds, then ends the AuthKit session when one was recorded,
 * returning to the login surface either way. The redirect runs even when the
 * revoke request or the purge fails, so a flaky network never leaves the user
 * on a page that still looks signed in.
 */
export async function performSignOut(options: SignOutOptions): Promise<void> {
  const {
    backendOrigin,
    fetchImpl = fetch,
    readStorage = () => window.localStorage,
    location = window.location,
    purgeStorage = purgeWorkspaceStorage,
    revokeTimeoutMs = 8_000,
  } = options;
  // The domain API user token was minted for this session; it must not
  // outlive the sign-out in this tab, whatever happens to the requests below.
  invalidateDomainApiSession();
  heldThreadTimelines.clear();
  // Revokes the workspace session and expires the cookie server-side. The
  // request is not aborted on timeout (the server may still be revoking);
  // the client just stops waiting and reports that sign-out is not done —
  // without redirecting, since the session may well still be live.
  const revoke = fetchImpl(`${backendOrigin}/api/amoreg/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
  /** The leaving half: forget the WorkOS session id, purge, hand over, go. */
  const finish = async (): Promise<void> => {
    if (signOutFinished) return;
    signOutFinished = true;
    let workosSessionId: string | null = null;
    try {
      const storage = readStorage();
      workosSessionId = storage.getItem(WORKOS_SESSION_STORAGE_KEY);
      storage.removeItem(WORKOS_SESSION_STORAGE_KEY);
    } catch {
      // Storage unavailable — skip the WorkOS hop.
    }
    // Drafts, stash, history, panel state and cached thread snapshots must not
    // outlive the session on this browser profile — purge before leaving.
    try {
      await purgeStorage();
    } catch {
      // The purge is best effort; the redirect is not.
    }
    if (workosSessionId) {
      // End the AuthKit session too, or the next hosted sign-in silently
      // re-authenticates and sign-out looks like a no-op.
      const logout = new URL(WORKOS_LOGOUT_URL);
      logout.searchParams.set("session_id", workosSessionId);
      logout.searchParams.set("return_to", `${location.origin}/pair`);
      location.assign(logout.toString());
    } else {
      location.assign("/pair");
    }
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = await Promise.race([
    revoke.then(
      () => false,
      () => false,
    ),
    new Promise<true>((resolve) => {
      timer = setTimeout(() => resolve(true), revokeTimeoutMs);
    }),
  ]).finally(() => clearTimeout(timer));
  if (timedOut) {
    // The request is still in flight. Should it succeed later, the session
    // is revoked server-side while this page still holds workspace content —
    // so any late 2xx finishes the sign-out (whichever attempt it belonged
    // to; `finish` runs once). A late failure changes nothing.
    void revoke.then(
      (response) => (response.ok ? finish() : undefined),
      () => undefined,
    );
    throw new SignOutTimeoutError();
  }
  try {
    // A revoke that answered with an error still ends in the redirect (the
    // cookie is gone or expiring either way); the error surfaces afterwards.
    await revoke;
  } finally {
    await finish();
  }
}
